import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { ArrowForward, SwapVert } from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import {
  IProcurementRequirementItem,
  IProcurementRequirementItems,
} from 'types/lot/procurement-requirement-items'
import { useNavigate } from 'react-router-dom'
import { CustomPagination } from 'components/purchase/CustomPagination'
import { useQueryState } from 'react-router-use-location-state'
import { useTranslation } from 'react-i18next'

const capitalize: Function = (text: string) => {
  return text.charAt(0).toLocaleUpperCase('az') + text.slice(1).toLocaleLowerCase()
}

function Row(props: { row: IProcurementRequirementItem; index: number }) {
  const { row, index } = props
  const { t } = useTranslation()

  return (
    <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
      <TableCell align="center">{index + 1}</TableCell>
      <TableCell>{row.name}</TableCell>
      <TableCell>
        <Tooltip
          placement="top"
          title={
            <React.Fragment>
              <Box sx={{ background: '#000', padding: '5px' }}>
                {row.description}
              </Box>
            </React.Fragment>
          }
        >
          <Typography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              webkitBoxOrient: 'vertical',
              overflowWrap: 'break-word',
              fontSize: '14px !important',
              fontWeight: 'normal',
              display: 'block',
              width: '200px',
              whiteSpace: 'nowrap',
            }}
          >
            {row.description}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>{row.quantity}</TableCell>
      <TableCell>{capitalize(t(row.measurement))}</TableCell>
    </TableRow>
  )
}

type Props = {
  data?: IProcurementRequirementItems
  lotId?: string
}

export function CompetitionDetailTable({ data, lotId }: Props) {
  const navigate = useNavigate()
  const rowsPerPage = 10
  const { t } = useTranslation()
  const [page, setPage] = useQueryState('page', 0)

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Box
        sx={{
          width: '100%',
          display: 'table',
          tableLayout: 'fixed',
        }}
      >
        <TableContainer component={Box}>
          <Table
            size="small"
            sx={{
              background: '#fff',
            }}
          >
            <TableHead
              sx={{
                background: '#fff',
              }}
            >
              <TableRow>
                <TableCell align="center">
                  <SwapVert
                    sx={{
                      color: '#8a94a6',
                      fontSize: '22px',
                    }}
                  />
                </TableCell>
                <TableCell>{t('title')}</TableCell>
                <TableCell>{t('description')}</TableCell>
                <TableCell>{t('quantity')}</TableCell>
                <TableCell>{t('measurement')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data!.items.map((row, index) => (
                <Row key={row.id} row={row} index={index} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {lotId ? (
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ marginTop: '24px' }}
          >
            <Typography variant="h6">
              {`${t('shown')} ${data?.info.actualPageSize} / ${data?.info.totalRows} ${t('stroke')}`}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover h6': {
                  color: 'rgb(3, 34, 76)',
                },
                '&:hover svg': {
                  color: 'rgb(3, 34, 76)',
                },
              }}
            >
              <Typography
                sx={{
                  cursor: 'pointer',
                }}
                variant="h6"
                onClick={() => navigate(`/competitions/products/${lotId}`)}
              >
                {t('view-all')}
              </Typography>

              <ArrowForward
                sx={{
                  fontSize: '14px',
                  color: '#8a94a6',
                  marginLeft: '3px',
                }}
              />
            </Box>
          </Box>
        ) : (
          <>
            {data?.info.totalRows !== 0 && (
              <CustomPagination
                total={data?.info.totalRows!}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
